// InputPageIndividualBRT.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './InputPage.css';

function InputPageIndividualBRT() {
  const location = useLocation();
  const navigate = useNavigate();

  const initialData = location.state?.formData || {
    transactionType: '',
    rentalIncome: '',
    loanAmount: '',
    propertyValue: '',
    productType: '',
    taxRate: '',
  };

  const [transactionType, setTransactionType] = useState(initialData.transactionType);
  const [monthlyRentalIncome, setMonthlyRentalIncome] = useState(initialData.rentalIncome);
  const [requiredLoanAmount, setRequiredLoanAmount] = useState(initialData.loanAmount);
  const [propertyValue, setPropertyValue] = useState(initialData.propertyValue);
  const [ltv, setLtv] = useState('');
  const [productType, setProductType] = useState(initialData.productType);
  const [taxRate, setTaxRate] = useState(initialData.taxRate);

  const loanRef = useRef(null);
  const valueRef = useRef(null);
  const productRef = useRef(null);
  const taxRateRef = useRef(null);

  const formatWithCommas = (value) => {
    if (!value) return '';
    const number = parseFloat(value.toString().replace(/,/g, ''));
    if (isNaN(number)) return '';
    return number.toLocaleString('en-UK');
  };

  useEffect(() => {
    const loan = parseFloat(requiredLoanAmount?.toString().replace(/,/g, ''));
    const value = parseFloat(propertyValue?.toString().replace(/,/g, ''));
    if (!isNaN(loan) && !isNaN(value) && value > 0) {
      const ltvCalc = ((loan / value) * 100).toFixed(2);
      setLtv(`${ltvCalc}%`);
    } else {
      setLtv('');
    }
  }, [requiredLoanAmount, propertyValue]);

  const handleSubmit = () => {
    // Check if all required fields are filled
    if (!transactionType || !monthlyRentalIncome || !requiredLoanAmount || !propertyValue || !productType || !taxRate) {
      alert('Please fill in all required fields before proceeding.');
      return;
    }

    const formData = {
      transactionType,
      rentalIncome: monthlyRentalIncome || '0',
      loanAmount: requiredLoanAmount || '0',
      propertyValue: propertyValue || '0',
      productType,
      taxRate,
    };
    navigate('/results-individual', { state: { formData } });
  };

  return (
    <div style={{
      height: '100vh', // Fixed viewport height
      display: 'flex',
      flexDirection: 'column',
      position: 'relative'
    }}>
      {/* Header */}
      <div className="header">
        <h1>Individual Buy to Let Calculator</h1>
        <p>Enter The Details Below Relating to your Individual BRT/HRT Enquiry</p>
      </div>

      {/* Main Content Area */}
      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        position: 'relative',
        overflow: 'auto' // Allow scrolling if content is too tall
      }}>
        {/* Back Button - Positioned equidistant between form and screen edge */}
        <div style={{
          position: 'absolute',
          right: 'calc((100vw - 600px) / 4 - 54px)', // Centers the 108px button in the space
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 1000
        }}>
          <button
            style={{ 
              backgroundColor: 'transparent',
              color: '#157DEC', 
              padding: '10px',
              borderRadius: '6px',
              border: '2px double #157DEC',
              fontFamily: 'Century Gothic, sans-serif',
              fontWeight: 'bold',
              cursor: 'pointer',
              fontSize: '14px',
              boxShadow: '0 0 0 1px rgba(21, 125, 236, 0.36), 0 0 6px rgba(21, 125, 236, 0.48), 0 0 12px rgba(21, 125, 236, 0.24), 0 0 18px rgba(21, 125, 236, 0.12)',
              width: '108px',
              height: '108px',
              textAlign: 'center',
              whiteSpace: 'normal',
              lineHeight: '1.1',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.3s ease',
              transform: 'translateY(0px)'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-3px) scale(1.05)';
              e.target.style.boxShadow = '0 0 0 2px rgba(21, 125, 236, 0.5), 0 0 8px rgba(21, 125, 236, 0.6), 0 0 16px rgba(21, 125, 236, 0.4), 0 0 24px rgba(21, 125, 236, 0.2)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0px) scale(1)';
              e.target.style.boxShadow = '0 0 0 1px rgba(21, 125, 236, 0.36), 0 0 6px rgba(21, 125, 236, 0.48), 0 0 12px rgba(21, 125, 236, 0.24), 0 0 18px rgba(21, 125, 236, 0.12)';
            }}
            onClick={() => navigate('/calculator-suite')}
          >
            ← Back to Calculator Selection
          </button>
        </div>

        {/* Form Container - Centered */}
        <div className="form-wrapper" style={{
          maxWidth: '600px',
          width: '100%',
          margin: '0 auto'
        }}>
          <div className="input-group">
            <label>Transaction Type *</label>
            <select 
              value={transactionType} 
              onChange={(e) => setTransactionType(e.target.value)}
              style={{
                borderColor: !transactionType ? '#ff6b6b' : '#ccc'
              }}
            >
              <option value="">Select...</option>
              <option value="Purchase">Purchase</option>
              <option value="Remo £4£">Remo £4£</option>
              <option value="Remo Cap Raising">Remo Cap Raising</option>
            </select>
          </div>

          <div className="input-group">
            <label>Monthly Rental Income (£) *</label>
            <input
              type="text"
              value={formatWithCommas(monthlyRentalIncome)}
              onChange={(e) => {
                const raw = e.target.value.replace(/,/g, '');
                if (!isNaN(raw)) setMonthlyRentalIncome(raw);
              }}
              onKeyDown={(e) => e.key === 'Enter' && loanRef.current.focus()}
              style={{
                borderColor: !monthlyRentalIncome ? '#ff6b6b' : '#ccc'
              }}
            />
          </div>

          <div className="input-group">
            <label>Required Loan Amount (£) *</label>
            <input
              type="text"
              value={formatWithCommas(requiredLoanAmount)}
              onChange={(e) => {
                const raw = e.target.value.replace(/,/g, '');
                if (!isNaN(raw)) setRequiredLoanAmount(raw);
              }}
              ref={loanRef}
              onKeyDown={(e) => e.key === 'Enter' && valueRef.current.focus()}
              style={{
                borderColor: !requiredLoanAmount ? '#ff6b6b' : '#ccc'
              }}
            />
          </div>

          <div className="input-group">
            <label>Property Value (£) *</label>
            <input
              type="text"
              value={formatWithCommas(propertyValue)}
              onChange={(e) => {
                const raw = e.target.value.replace(/,/g, '');
                if (!isNaN(raw)) setPropertyValue(raw);
              }}
              ref={valueRef}
              onKeyDown={(e) => e.key === 'Enter' && productRef.current.focus()}
              style={{
                borderColor: !propertyValue ? '#ff6b6b' : '#ccc'
              }}
            />
          </div>

          <div className="input-group">
            <label>Calculated LTV (%)</label>
            <input type="text" value={ltv} readOnly tabIndex={-1} />
          </div>

          <div className="input-group">
            <label>Product Type *</label>
            <select
              value={productType}
              onChange={(e) => setProductType(e.target.value)}
              ref={productRef}
              onKeyDown={(e) => e.key === 'Enter' && taxRateRef.current.focus()}
              style={{
                borderColor: !productType ? '#ff6b6b' : '#ccc'
              }}
            >
              <option value="">Select...</option>
              <option value="1 Year Fixed">1 Year Fixed</option>
              <option value="2 Year Fixed">2 Year Fixed</option>
              <option value="3 Year Fixed">3 Year Fixed</option>
              <option value="5 Year Fixed">5 Year Fixed</option>
              <option value="7 Year Fixed">7 Year Fixed</option>
              <option value="10 Year Fixed">10 Year Fixed</option>
              <option value="Variable/Tracker">Variable/Tracker</option>
            </select>
          </div>

          <div className="input-group">
            <label>Tax Rate *</label>
            <select
              value={taxRate}
              onChange={(e) => setTaxRate(e.target.value)}
              ref={taxRateRef}
              style={{
                borderColor: !taxRate ? '#ff6b6b' : '#ccc'
              }}
            >
              <option value="">Select...</option>
              <option value="Basic Rate Taxpayer">Basic Rate Taxpayer</option>
              <option value="Higher/Additional Rate Taxpayer">Higher/Additional Rate Taxpayer</option>
            </select>
          </div>

          <button className="go-to-results" onClick={handleSubmit}>
            Go To Results
          </button>
        </div>
      </div>

      {/* Footer - Fixed at bottom with original blue styling */}
      <div className="footer" style={{
        marginTop: 'auto',
        flexShrink: 0
      }}>
        © The BTL Calculator Company Ltd. All Rights Reserved 2025
        <br />
        Not for public use. For professional intermediaries use only.
      </div>
    </div>
  );
}

export default InputPageIndividualBRT;